import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { useEffect, useState } from 'react';
import { List, Map, OrderedMap } from 'immutable';
import {
    addQueueMembers,
    fetchAllowedQueueMembers,
} from 'flow/actions/pbx-actions';
import FilterSearchBar from 'flow/components/FilterSearchBar';
import SettingRow from 'flow/components/Setting/SettingRow';
import BaseModal from 'flow/components/ui-components/BaseModal';
import ContactAvatar from 'flow/components/ui-components/ContactAvatar';
import Divider from 'flow/components/ui-components/Divider';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import Loading from 'flow/components/ui-components/Loading';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { selectQueueMembers } from 'flow/selectors/queue-selectors';
import { ExtensionKey, QueueKey } from 'flow/types/EntityKeys';
import { Extension } from 'flow/types/Extension';

import { TypedMap } from 'flow/types/TypedMap';
import { extractName, sortByName } from 'flow/utils';
import {
    filterExtensionsBySearchableInfo,
    getFixedNumberFromContact,
    getMobileNumberFromContact,
} from 'flow/utils/extension-utils';
import { twMerge } from 'tailwind-merge';

interface Props {
    queueKey?: QueueKey;
}

const AddQueueMemberModal = ({ queueKey }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { openModal } = useModal();

    const extensions = useAppSelector((state) =>
        state.extensions.get('extensions'),
    );
    const allowedQueueMembers = useAppSelector((state) =>
        state.pbx.getIn(['queues', queueKey, 'allowedQueueMembers']),
    );
    const queueMembers = useAppSelector((state) =>
        selectQueueMembers(state, queueKey),
    );
    const queueWaves = useAppSelector((state) =>
        state.pbx.getIn(['queues', queueKey, 'queueWaves']),
    );

    const [filterText, setFilterText] = useState('');
    const [queueMemberList, setQueueMemberList] = useState(List());

    useEffect(() => {
        dispatch(fetchAllowedQueueMembers(queueKey));
    }, [dispatch, queueKey]);

    if (!extensions || !queueMembers) {
        return <Loading />;
    }

    const allowedExtensionKeys = allowedQueueMembers
        ? allowedQueueMembers.map((extension) => extension.get('key')).toSet()
        : List();

    const filteredExtensions = extensions
        .filter((extension) =>
            allowedExtensionKeys.contains(extension.get('key')),
        )
        .filter((extension) =>
            queueWaves && queueWaves.size > 0
                ? true
                : !queueMembers
                      .map((member) => member.get('extensionKey'))
                      .toSet()
                      .contains(extension.get('key')),
        );

    const unSelectedExtensions = filteredExtensions.filter(
        (extension) => !queueMemberList.contains(extension.get('key')),
    );

    const addedMembers = queueMemberList.reduce((acc, key) => {
        const member = filteredExtensions
            .reduce((acc, extension) => {
                return acc.set(extension.get('key'), extension);
            }, Map())
            .get(key) as TypedMap<Extension>;
        return acc.set(member.get('key'), member);
    }, OrderedMap());

    const openQueueWavePicker = () => {
        const allowedQueueWaves = queueWaves.filter((queueWave) => {
            const queueWaveMemberKeys = queueWave
                .get('queueWaveMappingDTOs')
                .map((queueWaveMapping) =>
                    queueWaveMapping.get('queueMember').get('extensionKey'),
                );
            return queueMemberList.every(
                (queueMemberKey) =>
                    !queueWaveMemberKeys.contains(queueMemberKey),
            );
        });

        openModal('addQueueWaveMember', {
            queueKey,
            queueWaves: allowedQueueWaves,
            members: getQueueMembersDTO(),
        });
    };

    const getQueueMembersDTO = () =>
        extensions
            .filter((extension) =>
                queueMemberList.contains(extension.get('key')),
            )
            .map((extension) => ({
                extensionKey: extension.get('key'),
                loggedIn: true,
                useMEXA: true,
            }))
            .toArray();

    const handleSelectClick = (extensionKey: ExtensionKey) => {
        setQueueMemberList(queueMemberList.push(extensionKey));
        setFilterText('');
    };

    return (
        <BaseModal
            open={true}
            header={i18next.t('Queue.HandleMembers.AddMembers')}
            confirmButtonText={i18next.t('Queue.HandleMembers.Add')}
            className="h-[39.5rem] w-[600px]"
            onContinue={
                queueWaves && queueWaves.size > 0 ? openQueueWavePicker : null
            }
            onSubmit={
                queueWaves && queueWaves.size > 0
                    ? null
                    : () => {
                          dispatch(
                              addQueueMembers(queueKey, getQueueMembersDTO()),
                          );
                      }
            }
        >
            <div className="flex flex-col gap-3">
                <FilterSearchBar
                    searchText={filterText}
                    placeholder={i18next.t('input.filter')}
                    handleFilter={(event, value) => {
                        setFilterText(value);
                    }}
                />

                {!addedMembers.isEmpty() ? (
                    <div className="flex flex-wrap gap-3">
                        {addedMembers
                            .map((extension: TypedMap<Extension>) => (
                                <div
                                    key={extension.get('key')}
                                    className="flex items-center rounded-sm border border-gray-300 p-1"
                                >
                                    <ContactAvatar
                                        contact={extension.get('contact')}
                                        size="small"
                                        fallBackBackgroundColorClass={twMerge(
                                            'group-hover:bg-gray-300 bg-gray-200',
                                        )}
                                        lazyLoad={true}
                                    />
                                    <div className="ms-2 me-3 text-sm">
                                        {extension.getIn(
                                            ['contact', 'firstName'],
                                            '',
                                        ) ||
                                            getFixedNumberFromContact(
                                                extension.get('contact'),
                                            ) ||
                                            getMobileNumberFromContact(
                                                extension.get('contact'),
                                            ) ||
                                            null}
                                    </div>
                                    <div>
                                        <IconButton
                                            aria-label={i18next.t('remove')}
                                            className="size-6"
                                            onClick={() =>
                                                setQueueMemberList(
                                                    queueMemberList.filter(
                                                        (key) =>
                                                            key !==
                                                            extension.get(
                                                                'key',
                                                            ),
                                                    ) as List<unknown>,
                                                )
                                            }
                                        >
                                            <Icon iconKey="cancel" />
                                        </IconButton>
                                    </div>
                                </div>
                            ))
                            .valueSeq()}
                    </div>
                ) : null}

                <Divider className="my-0" />

                {!unSelectedExtensions.isEmpty()
                    ? filterExtensionsBySearchableInfo(
                          unSelectedExtensions.sort(sortByName),
                          filterText,
                      )
                          .map((extension: TypedMap<Extension>) => (
                              <SettingRow
                                  key={extension.get('key')}
                                  className="h-12"
                                  cta={
                                      <IconButton
                                          aria-label={i18next.t(
                                              'chatsettings.add',
                                          )}
                                          className="reveal-on-hover-or-focus flex"
                                          onClick={() =>
                                              handleSelectClick(
                                                  extension.get('key'),
                                              )
                                          }
                                      >
                                          <Icon iconKey="add" />
                                      </IconButton>
                                  }
                              >
                                  <ContactAvatar
                                      contact={extension.get('contact')}
                                      size="small"
                                      fallBackBackgroundColorClass={twMerge(
                                          'group-hover:bg-gray-300 bg-gray-200',
                                      )}
                                  />

                                  <div className="truncate text-gray-800">
                                      {extractName(
                                          extension.get('contact'),
                                          i18next.t('status.unknown'),
                                      )}
                                  </div>
                              </SettingRow>
                          ))
                          .valueSeq()
                    : null}
            </div>
        </BaseModal>
    );
};

export default AddQueueMemberModal;
